import { Outlet } from 'react-router-dom';
import styled from '@emotion/styled';
import Sidebar from './Sidebar';
import { colors } from '../theme/tokens';
import { useState, useEffect } from 'react';
import { List } from '@phosphor-icons/react';

interface MainContentProps {
  sidebarCollapsed: boolean;
  isMobile: boolean;
}

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${colors.neutral.offWhite};
  width: 100%;
  box-sizing: border-box;
`;

const MobileHeader = styled.header`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background-color: ${colors.neutral.white};
  padding: 0 1rem;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    display: flex;
  }
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: ${colors.neutral.charcoal};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${colors.brand.laurel};
  }
`;

const MainContent = styled.main<MainContentProps>`
  flex: 1;
  padding: 1rem;
  margin-left: ${props => props.sidebarCollapsed ? '60px' : '20%'};
  transition: margin-left 0.3s ease;
  min-height: 100vh;
  box-sizing: border-box;
  width: calc(100% - ${props => props.sidebarCollapsed ? '60px' : '20%'});
  max-width: 100%;
  overflow-x: hidden;
  padding-top: ${props => props.isMobile ? '80px' : '1rem'};

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }
`;

const Layout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSidebarToggle = (collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
  };

  const handleHamburgerClick = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <LayoutContainer>
      {isMobile && (
        <MobileHeader>
          <HamburgerButton onClick={handleHamburgerClick}>
            <List size={24} weight="bold" />
          </HamburgerButton>
        </MobileHeader>
      )}
      <Sidebar 
        onToggle={handleSidebarToggle} 
        isMobile={isMobile}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      <MainContent sidebarCollapsed={isSidebarCollapsed} isMobile={isMobile}>
        <Outlet />
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout; 